/**
 * Live preview for a beta-lane contract: the editor's values are debounced
 * into `contracts.ir_preview` (the server compiles the document IR with the
 * same registry the PDF path uses) and the returned HTML is shown in a
 * sandboxed iframe styled by the IR theme — isolated from the app's CSS,
 * with no scripts.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { previewThemeCss } from '@hyranai/regta-document-ir';
import { tenantApi, type IrDiagnostic, type IrPreview, type PartySnapshotInput } from '../../api.js';
import { irErrorText } from '../../lib/ir-errors.js';
import { partitionDiagnostics, type TouchState } from '../../lib/form-touched.js';
import { previewSrcDoc } from './form-model.js';
import type { FieldTarget } from '../../components/beta-review/FieldActions.js';
import { markerTitle, type PreviewMarker } from '../../components/beta-review/review-model.js';
import { PreviewZoomBar, usePreviewZoom } from '../../components/PreviewZoom.js';
import {
  A4_WIDTH_PX,
  SHEET_GUTTER_PX,
  fitScale,
  resolveScale,
  scaledLayout,
} from '../../lib/preview-zoom.js';

const DEBOUNCE_MS = 450;

export type IrPreviewState = {
  data: IrPreview | null;
  error: string | null;
  loading: boolean;
};

export type UnsavedParties = { add: PartySnapshotInput[]; remove: string[] };

/**
 * `live` sends the editor's values and unsaved party edits (laid over the
 * stored ones server-side, nothing written); otherwise the server answers
 * from the stored contract or its issued revision. `refreshKey` re-fetches
 * when server-side inputs (parties, properties, attachments) change.
 */
export function useIrPreview(
  contractId: string | null,
  values: Record<string, unknown>,
  live: boolean,
  refreshKey: string,
  unsavedParties?: UnsavedParties,
): IrPreviewState {
  const [state, setState] = useState<IrPreviewState>({ data: null, error: null, loading: false });
  const seq = useRef(0);
  const fetchedOnce = useRef(false);
  const valuesJson = live ? JSON.stringify(values) : '';
  const partiesJson =
    live && unsavedParties && (unsavedParties.add.length > 0 || unsavedParties.remove.length > 0)
      ? JSON.stringify(unsavedParties)
      : '';

  useEffect(() => {
    if (!contractId) return;
    const my = ++seq.current;
    setState((s) => ({ ...s, loading: true }));
    const timer = setTimeout(
      () => {
        fetchedOnce.current = true;
        const parties = partiesJson ? (JSON.parse(partiesJson) as UnsavedParties) : null;
        const input = live
          ? {
              contractId,
              values: JSON.parse(valuesJson) as Record<string, unknown>,
              ...(parties?.add.length ? { parties_add: parties.add } : {}),
              ...(parties?.remove.length ? { parties_remove: parties.remove } : {}),
            }
          : { contractId };
        tenantApi.contracts
          .ir_preview(input)
          .then((data) => {
            if (seq.current === my) setState({ data, error: null, loading: false });
          })
          .catch((e: unknown) => {
            if (seq.current !== my) return;
            const message = irErrorText((e as Error).message, 'Kunde inte förhandsvisa dokumentet');
            setState((s) => ({ data: s.data, error: message, loading: false }));
          });
      },
      fetchedOnce.current ? DEBOUNCE_MS : 0,
    );
    return () => clearTimeout(timer);
  }, [contractId, valuesJson, partiesJson, live, refreshKey]);

  return state;
}

/**
 * The compiled document, one A4 sheet per part. `focus` scrolls the sheet
 * to a block instance (`data-instance-id`) when its `seq` changes.
 */
export function IrPreviewFrame({
  preview,
  focus,
  onValueClick,
  markers,
  onMarkerClick,
}: {
  preview: IrPreview;
  focus: { instanceId: string; seq: number } | null;
  /** Field navigation (ADR-0102 addendum 2): a click on a field-bound value; the rect is in viewport pixels. */
  onValueClick?: (target: FieldTarget, rect: DOMRect) => void;
  /** Open comments / suggestions shown in the sheet margin. */
  markers?: ReadonlyArray<PreviewMarker>;
  onMarkerClick?: (marker: PreviewMarker) => void;
}) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const valueClickRef = useRef(onValueClick);
  valueClickRef.current = onValueClick;
  const markerClickRef = useRef(onMarkerClick);
  markerClickRef.current = onMarkerClick;
  const markersRef = useRef(markers);
  markersRef.current = markers;
  const attachmentIds = useMemo(
    () => new Set((preview.attachments ?? []).map((a) => a.attachmentId)),
    [preview.attachments],
  );
  const attachmentIdsRef = useRef(attachmentIds);
  attachmentIdsRef.current = attachmentIds;
  const scrollY = useRef(0);
  const { zoom, setZoom } = usePreviewZoom();
  const zoomRef = useRef(zoom);
  const appliedScale = useRef<number | null>(null);
  const [fit, setFit] = useState(1);
  // Template bilagor are their own documents (ADR-0102 addendum 3), shown after the main one.
  const srcDoc = useMemo(
    () =>
      previewSrcDoc(
        [
          ...preview.parts,
          ...(preview.attachments ?? []).map((a) => ({
            key: a.attachmentId,
            title: a.title,
            html: a.html,
            letterheadHtml: a.letterheadHtml,
            footerHtml: a.footerHtml,
          })),
        ],
        preview.css,
        previewThemeCss,
      ),
    [preview.parts, preview.attachments, preview.css],
  );

  // Scale the sheet inside the frame: the body is taken out of flow and
  // transform-scaled, so the frame scrolls over the scaled size and
  // getBoundingClientRect (scroll-to-block) stays in visual pixels.
  const layout = useCallback((retry = true) => {
    const win = frameRef.current?.contentWindow;
    const doc = frameRef.current?.contentDocument;
    if (!win || !doc?.body) return;
    const avail = doc.documentElement.clientWidth;
    const page = doc.querySelector<HTMLElement>('.rd-page');
    const sheet = (page?.offsetWidth || A4_WIDTH_PX) + 2 * SHEET_GUTTER_PX;
    const f = fitScale(avail, sheet);
    const s = resolveScale(zoomRef.current, f);
    const { layoutWidth } = scaledLayout(avail, s, sheet);
    const b = doc.body.style;
    b.position = 'absolute';
    b.top = '0';
    b.left = '0';
    b.margin = '0';
    b.boxSizing = 'border-box';
    b.width = `${layoutWidth}px`;
    b.transformOrigin = '0 0';
    b.transform = `scale(${s})`;
    const prev = appliedScale.current;
    appliedScale.current = s;
    if (prev !== null && prev !== s) win.scrollTo(win.scrollX * (s / prev), win.scrollY * (s / prev));
    setFit(f);
    // A vertical scrollbar appearing after the rescale narrows the frame once.
    if (retry && doc.documentElement.clientWidth !== avail) layout(false);
    else placeMarkers(doc, markersRef.current, attachmentIdsRef.current, s);
  }, []);

  useEffect(() => {
    const doc = frameRef.current?.contentDocument;
    if (doc?.body && appliedScale.current !== null) {
      placeMarkers(doc, markers, attachmentIds, appliedScale.current);
    }
  }, [markers, attachmentIds]);

  useEffect(() => {
    zoomRef.current = zoom;
    layout();
  }, [zoom, layout]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const ro = new ResizeObserver(() => layout());
    ro.observe(frame);
    return () => ro.disconnect();
  }, [layout]);

  const onLoad = () => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    appliedScale.current = null;
    const doc = frameRef.current?.contentDocument;
    if (doc) wireReviewChrome(doc, attachmentIdsRef, valueClickRef, markerClickRef, markersRef, frameRef);
    layout();
    win.scrollTo(0, scrollY.current);
    win.addEventListener('scroll', () => {
      scrollY.current = win.scrollY;
    });
  };

  useEffect(() => {
    if (!focus) return;
    const win = frameRef.current?.contentWindow;
    const doc = frameRef.current?.contentDocument;
    if (!win || !doc) return;
    const el = doc.querySelector<HTMLElement>(`[data-instance-id="${CSS.escape(focus.instanceId)}"]`);
    if (!el) return;
    win.scrollTo({ top: el.getBoundingClientRect().top + win.scrollY - 16, behavior: 'smooth' });
    el.classList.add('bp-flash');
    const t = setTimeout(() => el.classList.remove('bp-flash'), 900);
    return () => clearTimeout(t);
  }, [focus]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PreviewZoomBar zoom={zoom} scale={resolveScale(zoom, fit)} onChange={setZoom} />
      <iframe
        ref={frameRef}
        title="Förhandsvisning"
        sandbox="allow-same-origin"
        srcDoc={srcDoc}
        onLoad={onLoad}
        className="min-h-0 w-full flex-1 border-0 bg-stone-100"
      />
    </div>
  );
}

const SEVERITY_CLS: Record<IrDiagnostic['severity'], string> = {
  error: 'border-red-300 bg-red-50 text-red-800',
  warning: 'border-amber-300 bg-amber-50 text-amber-900',
};

/**
 * Compiler diagnostics. Value-level ones (tied to a field) follow the
 * form's touched rule — on a new document they stay hidden until the
 * field is touched or a step transition reveals every gap; a slot-scoped
 * one links to its field. Structural ones (a missing party, an unknown
 * block) are always listed, as a quiet notice rather than an error.
 */
export function IrDiagnostics({
  diagnostics,
  touch,
  fieldLabel,
  onLocateField,
}: {
  diagnostics: ReadonlyArray<IrDiagnostic>;
  touch: TouchState;
  fieldLabel: (key: string) => string | null;
  onLocateField: (key: string) => void;
}) {
  const { value, structural } = partitionDiagnostics(diagnostics, touch);
  if (value.length === 0 && structural.length === 0) return null;
  const errors = value.filter((d) => d.severity === 'error').length;
  return (
    <div className="border-b border-stone-200 bg-white px-4 py-2 text-xs">
      {structural.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-stone-600">
          {structural.map((d, i) => (
            <li key={`${d.code}-${d.instance_id ?? ''}-${i}`} className="flex items-baseline gap-1.5">
              <span aria-hidden="true" className="text-stone-400">•</span>
              <span>{d.message}</span>
            </li>
          ))}
        </ul>
      )}
      {value.length > 0 && (
        <details open={errors > 0} className={structural.length > 0 ? 'mt-2' : ''}>
          <summary className="cursor-pointer select-none font-medium text-stone-700">
            Diagnostik{' '}
            <span className="font-normal text-stone-500">
              ({errors} fel, {value.length - errors} varningar)
            </span>
          </summary>
          <ul className="mt-2 flex max-h-40 flex-col gap-1 overflow-auto">
            {value.map((d, i) => {
              const label = d.slot_key ? fieldLabel(d.slot_key) : null;
              return (
                <li
                  key={`${d.code}-${d.instance_id ?? ''}-${d.slot_key ?? ''}-${i}`}
                  className={`rounded border px-2 py-1 ${SEVERITY_CLS[d.severity]}`}
                >
                  {label && d.slot_key ? (
                    <button
                      type="button"
                      onClick={() => onLocateField(d.slot_key!)}
                      className="mr-1 font-medium underline decoration-dotted underline-offset-2"
                    >
                      {label}:
                    </button>
                  ) : null}
                  {d.message}
                  <span className="ml-1 font-mono text-[10px] opacity-60">{d.code}</span>
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </div>
  );
}

const REVIEW_CHROME_CSS =
  '.bp-values [data-key]{cursor:pointer;border-radius:2px}' +
  '.bp-values [data-key]:hover{outline:2px solid #f59e0b;outline-offset:1px;background:#fef3c7}' +
  '.rd-page{position:relative}' +
  '.bp-marker{position:absolute;right:-11px;z-index:5;width:22px;height:22px;padding:0;border:1px solid #f59e0b;border-radius:11px 11px 11px 2px;' +
  'background:#fffbeb;color:#92400e;font:600 11px/20px system-ui,sans-serif;text-align:center;cursor:pointer;box-shadow:0 1px 2px rgba(0,0,0,.12)}' +
  '.bp-marker[data-kind=suggestion]{border-color:#dc2626;background:#fef2f2;color:#991b1b}' +
  '.bp-marker:hover{filter:brightness(.95)}';

/** Value clicks + marker clicks, delegated once per loaded document (the frame runs no scripts). */
function wireReviewChrome(
  doc: Document,
  attachmentIds: { current: ReadonlySet<string> },
  onValue: { current: ((target: FieldTarget, rect: DOMRect) => void) | undefined },
  onMarker: { current: ((marker: PreviewMarker) => void) | undefined },
  markers: { current: ReadonlyArray<PreviewMarker> | undefined },
  frame: { current: HTMLIFrameElement | null },
): void {
  if (!doc.getElementById('bp-review-chrome')) {
    const style = doc.createElement('style');
    style.id = 'bp-review-chrome';
    style.textContent = REVIEW_CHROME_CSS;
    doc.head.append(style);
  }
  doc.body.classList.toggle('bp-values', !!onValue.current);
  doc.addEventListener('click', (e) => {
    const el = e.target as Element | null;
    const marker = el?.closest?.<HTMLElement>('.bp-marker');
    if (marker) {
      const m = markers.current?.find((x) => x.id === marker.dataset.markerId);
      if (m) onMarker.current?.(m);
      return;
    }
    const value = el?.closest?.<HTMLElement>('[data-key]');
    if (!value || !onValue.current) return;
    const key = value.dataset.key;
    if (!key) return;
    const part = value.closest<HTMLElement>('[data-part]')?.dataset.part ?? '';
    const r = value.getBoundingClientRect();
    const f = frame.current?.getBoundingClientRect();
    const rect = new DOMRect((f?.left ?? 0) + r.left, (f?.top ?? 0) + r.top, r.width, r.height);
    onValue.current({ key, attachmentId: attachmentIds.current.has(part) ? part : null }, rect);
  });
}

/** Margin markers beside their target; re-placed after every layout. */
function placeMarkers(
  doc: Document,
  markers: ReadonlyArray<PreviewMarker> | undefined,
  attachmentIds: ReadonlySet<string>,
  scale: number,
): void {
  for (const old of Array.from(doc.querySelectorAll('.bp-marker'))) old.remove();
  if (!markers?.length) return;
  const pagesOf = (documentKey: string): HTMLElement[] =>
    Array.from(doc.querySelectorAll<HTMLElement>('.rd-page[data-part]')).filter((p) => {
      const part = p.dataset.part ?? '';
      return documentKey === 'main' ? !attachmentIds.has(part) : part === documentKey;
    });
  const taken = new Map<HTMLElement, number[]>();
  for (const m of markers) {
    const pages = pagesOf(m.documentKey);
    if (pages.length === 0) continue;
    let target: HTMLElement | null = null;
    for (const page of pages) {
      target =
        m.target.kind === 'field'
          ? page.querySelector<HTMLElement>(`[data-key="${CSS.escape(m.target.key)}"]`)
          : m.target.kind === 'instance'
            ? page.querySelector<HTMLElement>(`[data-instance-id="${CSS.escape(m.target.instanceId)}"]`)
            : null;
      if (target) break;
    }
    const page = (target?.closest<HTMLElement>('.rd-page') ?? pages[0])!;
    const pageTop = page.getBoundingClientRect().top;
    let top = target ? (target.getBoundingClientRect().top - pageTop) / (scale || 1) : 8;
    const used = taken.get(page) ?? [];
    while (used.some((t) => Math.abs(t - top) < 24)) top += 24;
    used.push(top);
    taken.set(page, used);
    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.className = 'bp-marker';
    btn.dataset.markerId = m.id;
    btn.dataset.kind = m.suggestions > 0 ? 'suggestion' : 'comment';
    btn.title = markerTitle(m);
    btn.setAttribute('aria-label', btn.title);
    btn.style.top = `${Math.max(0, top)}px`;
    btn.textContent = String(m.comments + m.suggestions + m.fieldRequests);
    page.append(btn);
  }
}
